import { User } from "firebase/auth"
import { get, getDatabase, ref, set, update } from "firebase/database"
import fbConf from "../FirebaseConf"
import VoltieUser, { Money } from "../types/VoltieUser"
import { defaultCurrencyForLanguage } from "./currency"

// Creates voltie/chargerusers/<uid> after every successful web sign-in, mirroring the iOS
// updateOrCreateUser(). Before VLT-2669 only the email/password signup wrote this node, so anyone who
// arrived through Google or Apple had a Firebase Auth record and no profile at all: no pushes, no
// statistics email, an empty admin sheet. An existing node is never overwritten - only absent fields
// are filled in, plus the login timestamps, which are meant to move on every login.

export type SignInProvider = 'password' | 'google' | 'apple'

const PROVIDER_ENUM: Record<SignInProvider, string> = {
  password: 'Email',
  google: 'Google',
  apple: 'Apple',
}

// Every push defaults on except charger_not_available, matching AppSettingsRepository on Android and
// the iOS defaults. newsletter is the exception the mobile default cannot decide: the web signup form
// asks for marketing consent explicitly, so it follows that checkbox and stays off without one.
const PUSH_DEFAULTS_ON = [
  'car_battery_level_reached',
  'car_not_charging',
  'car_plugged_in',
  'car_unplugged',
  'charger_errors',
  'charger_software_update',
  'charging_finished',
  'charging_not_started',
  'charging_started',
  'limited_charging_started',
  'scheduled_charging_started',
  'statistics_email',
] as const

function defaultPushNotifications(marketingAccepted: boolean): { [key: string]: boolean } {
  const defaults: { [key: string]: boolean } = { charger_not_available: false, newsletter: marketingAccepted }
  for (const key of PUSH_DEFAULTS_ON) defaults[key] = true
  return defaults
}

// Prices per currency as [electricity, petrol per litre, public charging], from the iOS
// CalculationValues.defaultData(for:) table that Android ported in VLT-2618. A currency without its own
// row keeps its code but borrows the EUR figures, as Android does - the point is that a non-Hungarian
// first run does not start from forint-calibrated numbers.
const CALCULATION_DEFAULTS_BY_CURRENCY: { [currency: string]: [number, number, number] } = {
  HUF: [70, 650, 225],
  EUR: [0.20, 1.80, 0.60],
  PLN: [0.90, 7.50, 2.50],
  RON: [1.00, 7.00, 2.50],
  TRY: [4.00, 40.00, 12.00],
}

function defaultCalculationValues(language: string): {
  average_consumption: number
  battery_size: number
  co2saving: number
  petrol_avg_consumption: number
  petrol_price: Money
  price: Money
  public_price: Money
} {
  const currency = defaultCurrencyForLanguage(language)
  const [price, petrolPrice, publicPrice] = CALCULATION_DEFAULTS_BY_CURRENCY[currency] ?? CALCULATION_DEFAULTS_BY_CURRENCY.EUR
  return {
    average_consumption: 16,
    battery_size: 60,
    co2saving: 150,
    petrol_avg_consumption: 8,
    petrol_price: { currency, value: petrolPrice },
    price: { currency, value: price },
    public_price: { currency, value: publicPrice },
  }
}

// Google and Apple hand back one display name; the profile stores the two halves separately.
function splitDisplayName(displayName: string | null): { first_name: string, last_name: string } {
  const parts = (displayName ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return { first_name: '', last_name: '' }
  return { first_name: parts[0], last_name: parts.slice(1).join(' ') }
}

function preferredLanguage(): string {
  return localStorage.getItem('preferedLanguage') || 'en'
}

// The web only ever asked for terms/privacy on the email signup form, so a first-time Google or Apple
// user reached the dashboard without accepting anything. An empty or absent timestamp means unaccepted.
export async function hasAcceptedTerms(uid: string): Promise<boolean> {
  const snapshot = await get(ref(getDatabase(fbConf), `voltie/chargerusers/${uid}/accepted_terms_version`))
  return hasTermsValue(snapshot.val())
}

function hasTermsValue(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0
}

// provider is null for a passkey sign-in: it has no provider_logins key of its own, and inventing one
// would put an unknown key under a node the RTDB .validate schema checks (VLT-2462).
export default async function ensureUserProfile(
  user: User,
  provider: SignInProvider | null,
  options?: { marketingAccepted?: boolean, acceptedTermsVersion?: string },
) {
  const profileRef = ref(getDatabase(fbConf), `voltie/chargerusers/${user.uid}`)
  const now = (new Date()).toISOString()
  const snapshot = await get(profileRef)
  const { first_name, last_name } = splitDisplayName(user.displayName)

  if (!snapshot.exists()) {
    await set(profileRef, {
      accepted_terms_version: options?.acceptedTermsVersion ?? '',
      birth_date: '',
      charger_id: '',
      email: user.email ?? '',
      fcm_tokens: {},
      first_name,
      last_name,
      nickname: user.displayName ?? '',
      photo_url: user.photoURL ?? '',
      platform_logins: { frontend: now },
      preferences: {
        additional_email: [],
        calculation_values: defaultCalculationValues(preferredLanguage()),
        language: preferredLanguage(),
        push_notifications: defaultPushNotifications(options?.marketingAccepted ?? false),
      },
      provider_enum: provider ? PROVIDER_ENUM[provider] : '',
      provider_logins: { apple: '', facebook: '', google: '', password: '', ...(provider ? { [provider]: now } : {}) },
      user_uid: user.uid,
    })
    return
  }

  // Slash-separated keys so each field merges on its own; a nested object would replace its whole parent.
  const existing = snapshot.val() as VoltieUser
  const updates: { [path: string]: unknown } = { 'platform_logins/frontend': now }
  if (provider) updates[`provider_logins/${provider}`] = now

  const fillIfAbsent = (path: string, current: unknown, value: unknown) => {
    if (current === undefined || current === null) updates[path] = value
  }

  // An empty string here means unaccepted, so fillIfAbsent would not catch it; only a fresh acceptance
  // passed by the caller writes this, and an existing timestamp is never overwritten.
  if (options?.acceptedTermsVersion && !hasTermsValue(existing.accepted_terms_version)) {
    updates['accepted_terms_version'] = options.acceptedTermsVersion
  }

  fillIfAbsent('user_uid', existing.user_uid, user.uid)
  fillIfAbsent('email', existing.email, user.email ?? '')
  if (provider) fillIfAbsent('provider_enum', existing.provider_enum, PROVIDER_ENUM[provider])
  fillIfAbsent('first_name', existing.first_name, first_name)
  fillIfAbsent('last_name', existing.last_name, last_name)
  fillIfAbsent('nickname', existing.nickname, user.displayName ?? '')
  fillIfAbsent('photo_url', existing.photo_url, user.photoURL ?? '')
  fillIfAbsent('birth_date', existing.birth_date, '')
  fillIfAbsent('charger_id', existing.charger_id, '')
  fillIfAbsent('preferences/language', existing.preferences?.language, preferredLanguage())
  fillIfAbsent('preferences/calculation_values', existing.preferences?.calculation_values, defaultCalculationValues(existing.preferences?.language ?? preferredLanguage()))

  const pushDefaults = defaultPushNotifications(options?.marketingAccepted ?? false)
  const existingPush = existing.preferences?.push_notifications as { [key: string]: boolean } | undefined
  for (const [key, value] of Object.entries(pushDefaults)) {
    fillIfAbsent(`preferences/push_notifications/${key}`, existingPush?.[key], value)
  }

  await update(profileRef, updates)
}
